import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API, axios, formatDateUS } from "@/api";
import { ArrowLeft, History, Coins, Loader2, Info, ChevronDown } from "lucide-react";

export function VerificationHistoryPage() {
  const navigate = useNavigate();
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/verifications/history`); setVerifications(res.data); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="verification-history-page">
      <div className="bg-white border-b border-slate-200 p-4 pt-6 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-slate-100" data-testid="back-btn"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
        <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><History className="w-5 h-5 text-sky-500" />Verification History</h1>
      </div>
      <div className="px-4 mt-4 space-y-2">
        {verifications.map((v) => (
          <div key={v.id} className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between"><h3 className="text-sm font-semibold text-slate-800">{v.business_name}</h3><span className={`text-xs font-medium px-2 py-1 rounded-full ${v.result === "confirmed" ? "bg-green-100 text-green-700" : v.result === "denied" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{v.result}</span></div>
            {v.notes && <p className="text-xs text-slate-500 mt-1">{v.notes}</p>}
            <div className="flex items-center justify-between mt-2"><span className="text-xs text-slate-400">{formatDateUS(v.created_at)}</span><span className="text-xs font-medium text-amber-600">+{v.points_awarded} pts</span></div>
          </div>
        ))}
        {verifications.length === 0 && <div className="text-center py-16 text-slate-400"><History className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium">No verifications yet</p></div>}
      </div>
    </div>
  );
}

export function PointsHistoryPage() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [histRes, guideRes] = await Promise.all([
          axios.get(`${API}/points/history`),
          axios.get(`${API}/points/guide`),
        ]);
        setHistory(histRes.data);
        setGuide(guideRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>;

  const totalPoints = history.reduce((acc, h) => acc + h.points, 0);

  const actionColors = {
    submission: "bg-purple-500",
    verification: "bg-sky-500",
    feedback: "bg-pink-500",
    claim: "bg-emerald-500",
  };

  const actionIcons = {
    submission: "S",
    verification: "V",
    feedback: "F",
    claim: "C",
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="points-history-page">
      <div className="bg-white border-b border-slate-200 p-4 pt-6 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-slate-100" data-testid="back-btn"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
        <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Coins className="w-5 h-5 text-amber-500" />Points History</h1>
        <span className="ml-auto text-lg font-bold text-amber-600">{totalPoints} pts</span>
      </div>

      {/* Points Guide Toggle */}
      <div className="px-4 mt-4">
        <button onClick={() => setShowGuide(!showGuide)} className="w-full bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-3 border border-amber-200 flex items-center justify-between" data-testid="points-guide-toggle">
          <span className="text-sm font-semibold text-amber-700 flex items-center gap-2"><Info className="w-4 h-4" />How to Earn Points</span>
          <ChevronDown className={`w-4 h-4 text-amber-500 transition-transform ${showGuide ? "rotate-180" : ""}`} />
        </button>
        {showGuide && guide && (
          <div className="mt-2 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" data-testid="points-guide-panel">
            {guide.actions.map((a, i) => (
              <div key={i} className={`flex items-center gap-3 px-4 py-2.5 ${i > 0 ? "border-t border-slate-50" : ""}`}>
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white ${actionColors[a.type] || "bg-slate-500"}`}>+{a.points}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{a.action}</p>
                  <p className="text-[10px] text-slate-400">{a.description}</p>
                </div>
              </div>
            ))}
            {guide.levels && (
              <div className="border-t border-slate-100 p-4">
                <p className="text-xs font-semibold text-slate-500 mb-2">LEVEL THRESHOLDS</p>
                <div className="flex gap-2">
                  {guide.levels.map((l) => (
                    <div key={l.name} className="flex-1 text-center bg-slate-50 rounded-lg p-2">
                      <p className="text-xs font-bold text-slate-700">{l.name}</p>
                      <p className="text-[10px] text-slate-400">{l.min_points}+ pts</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="px-4 mt-3 space-y-2 pb-8">
        {history.map((h) => (
          <div key={h.id} className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 flex items-center gap-3" data-testid={`point-event-${h.id}`}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white text-sm ${actionColors[h.action_type] || "bg-slate-500"}`}>
              {actionIcons[h.action_type] || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-slate-800 truncate">{h.business_name || h.action}</h3>
              <p className="text-xs text-slate-400">{h.action}{h.detail ? ` · ${h.detail}` : ""} · {formatDateUS(h.created_at)}</p>
            </div>
            <span className="text-sm font-bold text-amber-600 flex-shrink-0">+{h.points}</span>
          </div>
        ))}
        {history.length === 0 && <div className="text-center py-16 text-slate-400"><Coins className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium">No points earned yet</p><p className="text-xs mt-1">Verify deals, claim birthday deals, and submit new ones!</p></div>}
      </div>
    </div>
  );
}
